/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Flex, Modal, theme, Typography } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import {
	Component,
	ComponentTypes,
	createDrawerBaseClassName,
	DEFAULT_GAP_CHECKBOXES,
	defaultComponentDimensions,
	defaultHeight,
	defaultWidth,
	Dimensions,
	docgenFullScreenEditFlowId,
	getDefaultComponent,
	getDefaultComponentNames,
	getDrawerBodyElementSelector,
	getEditFlowElementById,
	getSignerColor,
	getSignerFields,
	GroupRequirement,
	isAnnotationComponent,
	OFFSET_X,
} from '../../common/componentUtils';
import { useAppStore } from '../../common/store';
import { OverlayerPage } from '../../components/OverlayerPage';
import { PDFViewer } from '../../components/PDFViewer';
import { DataPreFilledType } from '../../constants';
import { DocgenClient } from '../../data/docgenClient';
import { handleError, handleErrorWithServerMessage } from '../../data/errorHandler';
import { PatchPayloadItem } from '../../data/interfaces/APIRequest';
import { Role, Variable } from '../../data/interfaces/DocumentTemplate';
import { SchemaProperty } from '../../data/interfaces/SchemaResponse';
import { preparePathSlug } from '../../routes';
import { t } from '../../utils';
import { navigationEventManager } from '../../utils/navigationEventManager';
import { DocumentsPanel } from './DocumentsPanel';
import { LeftPanel } from './LeftPanel';
import {
	StyledDocumentViewer,
	StyledPrepareDocumentContainer,
} from './PrepareTemplate.styled';

const COMPONENT_INVALID_EVENT = 'COMPONENT_INVALID_EVENT';

const { Text } = Typography;

const useDocgenNavigation = (documentTemplate, modal) => {
	const { token } = theme.useToken();
	const { setIsLoading, updateTemplateMetadata } = useAppStore(state => ({
		setIsLoading: state.setIsLoading,
		updateTemplateMetadata: state.updateTemplateMetadata,
	}));

	if (documentTemplate?.rid) {
		const checkAllComponentsValid = (components): boolean => {
			const errorComponentsList = components?.filter(c => c.showErrorOnResizable) || [];

			if (errorComponentsList.length > 0) {
				const firstErrorComponent = sortedFirstInvalidComponents(errorComponentsList);
				notifyAndScrollToErrorComponent(firstErrorComponent);
				return false;
			}

			return true;
		};

		function notifyAndScrollToErrorComponent(firstErrorComponent): void {
			if (firstErrorComponent) {
				if (firstErrorComponent.type === ComponentTypes.SelectComponent) {
					handleError(t('dropdownInsufficientOptionsError'));
				} else if (firstErrorComponent.type === ComponentTypes.PaymentComponent) {
					handleError(t('paymentAmountInvalidError'));
				}

				const event = new CustomEvent(COMPONENT_INVALID_EVENT, {
					detail: firstErrorComponent,
				});
				document.dispatchEvent(event);
			}
		}

		//keep track of the first invalid component to highlight
		function sortedFirstInvalidComponents(errorComponentsList): Component {
			let firstComp = errorComponentsList[0];
			errorComponentsList.forEach(comp => {
				if (comp.page === firstComp.page) {
					if (comp.y === firstComp.y) {
						if (comp.x < firstComp.x) {
							firstComp = comp;
						}
					} else {
						if (comp.y < firstComp.y) {
							firstComp = comp;
						}
					}
				} else {
					if (comp.page < firstComp.page) {
						firstComp = comp;
					}
				}
			});
			return firstComp;
		}

		const SignersWithNoComponentContent = ({ signersWithoutComponents }) => {
			const signersWithNoComponents = signersWithoutComponents.map(signer => signer.name);
			return (
				<Flex
					vertical
					gap="middle"
					style={{ padding: `${token.paddingXS}px 0 ${token.paddingSM}px` }}
				>
					<Flex vertical gap="small">
						<Text style={{ marginBottom: 0 }}>
							{t('noSignerComponentInfoModal.subtitle')}
						</Text>
						<Text strong>{signersWithNoComponents.join(', ')}</Text>
					</Flex>
					<div>{t('noSignerComponentInfoModal.description')}</div>
				</Flex>
			);
		};

		const getSignersWithNoComponent = (components, roles) => {
			const componentsAssignedToSet = new Set(
				components?.map(item => item.assignedTo?.name) || []
			);

			const rolesNotPresent = roles?.filter(
				role => !componentsAssignedToSet.has(role.name)
			);

			return rolesNotPresent;
		};

		const handleWarningForSignersWithNoComponents = async (components, roles) => {
			const signersWithoutComponents = getSignersWithNoComponent(components, roles);

			if (signersWithoutComponents.length > 0) {
				const confirmed = await modal.confirm({
					title: t('noSignerComponentInfoModal.title'),
					icon: <ExclamationCircleFilled />,
					content: SignersWithNoComponentContent({
						signersWithoutComponents,
					}),
					okText: t('noSignerComponentInfoModal.confirmButtonText'),
					cancelText: t('noSignerComponentInfoModal.cancelButtonText'),
				});
				return confirmed;
			}

			return true;
		};

		const updateTemplate = async (
			documentTemplate,
			updateDocumentTemplate,
			updateTemplateMetadata,
			setIsLoading
		) => {
			setIsLoading(true);

			try {
				if (documentTemplate?.fields) {
					const payload: PatchPayloadItem[] = [
						{
							op: 'replace',
							path: '/fields',
							value: documentTemplate?.fields,
						},
					];

					const patchResponse = await DocgenClient.updateDocumentTemplate(
						payload,
						documentTemplate.rid
					);

					updateDocumentTemplate(patchResponse);
				}

				updateTemplateMetadata({ currentStep: 2 });
			} catch {
				handleErrorWithServerMessage(t('errorMessages.updateDocumentTemplateError'));
			} finally {
				setIsLoading(false);
			}
		};

		const onNext = async () => {
			const { documentTemplate, updateDocumentTemplate } = useAppStore.getState();

			const hasAtleastOneRole = documentTemplate?.roles?.length > 0;
			if (!hasAtleastOneRole) {
				handleError(t('hasAtleastOneRoleErrorMessage'));
				return;
			}

			if (!checkAllComponentsValid(documentTemplate.fields)) {
				return;
			}

			const isConfirmed = await handleWarningForSignersWithNoComponents(
				documentTemplate.fields,
				documentTemplate.roles
			);

			if (isConfirmed) {
				updateTemplate(
					documentTemplate,
					updateDocumentTemplate,
					updateTemplateMetadata,
					setIsLoading
				);
			}
		};

		const navigationEventObject = {
			onNext,
			onPrevious: () => {
				updateTemplateMetadata({ currentStep: 0 });
			},
		};

		navigationEventManager.register(preparePathSlug, navigationEventObject);
	}

	useEffect(() => {
		return () => {
			navigationEventManager.unregister(preparePathSlug);
		};
	}, []);
};

type PageMetaDataType = {
	pageNumber: number;
	scale: number;
	pageHeight: number;
	pageWidth: number;
};

export const Prepare = () => {
	const [modal, contextHolder] = Modal.useModal();
	const {
		documentTemplate,
		resizedComponents,
		setResizedComponents,
		updateDocumentTemplate,
	} = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		resizedComponents: state.resizedComponents,
		setResizedComponents: state.setResizedComponents,
		updateDocumentTemplate: state.updateDocumentTemplate,
	}));

	useDocgenNavigation(documentTemplate, modal);
	const signers = useMemo(() => {
		return documentTemplate?.roles || [];
	}, [documentTemplate?.roles]);

	const getSignerComponents = useCallback(
		(components: Component[]) => {
			return components.map(component => {
				const signerComponent = {
					...component,
					assignedTo:
						signers?.find(signer => signer.id === component?.assignedTo?.id) ?? null,
				};
				return signerComponent;
			});
		},
		[signers]
	);

	const componentsFromStore = useMemo(
		() => documentTemplate?.fields || [],
		[documentTemplate?.fields]
	);

	const [componentsToEdit, setComponentsToEdit] = React.useState<Component[]>([]);

	const [pageMetadata, setPageMetadata] = React.useState<PageMetaDataType | null>(null);
	const [selectedComponentType, setSelectedComponentType] =
		React.useState<ComponentTypes | null>(null);

	const components: Component[] = useMemo(() => {
		return componentsFromStore.length > 0 ? getSignerComponents(componentsFromStore) : [];
	}, [componentsFromStore, getSignerComponents]);

	const { dataUrl, user } = useAppStore(state => ({
		dataUrl: state.dataUrl,
		user: state.user,
	}));

	const pdfRef = useRef<HTMLDivElement>(null);

	const [selectedSigner, setSelectedSigner] = React.useState(
		signers.length > 0 ? signers[0] : null
	);

	const [selectedVariable, setSelectedVariable] = React.useState(
		documentTemplate?.variables?.length > 0 ? documentTemplate?.variables?.[0] : null
	);

	const [selectedProperty, setSelectedProperty] = React.useState<SchemaProperty>(null);

	const [scrollToPageNumber, setScrollToPageNumber] = React.useState(1);

	const getResizedWidthAndHeight = useCallback(
		(
			componentType: ComponentTypes,
			parentWidth: number,
			parentHeight: number,
			scaledWidth: number,
			scaledHeight: number
		) => {
			const component = resizedComponents?.get(componentType);

			const width = component ? component.width : scaledWidth / parentWidth;
			const height = component ? component.height : scaledHeight / parentHeight;

			return { width, height };
		},
		[resizedComponents]
	);

	const addNewComponent = useCallback(
		(newComponents: Component[]) => {
			updateDocumentTemplate({ fields: [...components, ...newComponents] });
		},
		[components, updateDocumentTemplate]
	);

	const generateGroupId = useCallback(() => {
		let maxCheckBoxGroupId = 0;
		components.forEach(component => {
			if (component.type === ComponentTypes.CheckGroupComponent) {
				maxCheckBoxGroupId = Math.max(maxCheckBoxGroupId, +component.groupId);
			}
		});
		return `${maxCheckBoxGroupId + 1}`;
	}, [components]);

	const getPreferredDateFormat = useCallback((): string => {
		const dateComponents = components.filter(
			component => component.type === ComponentTypes.DateComponent
		);
		let dateFormat = dateComponents[dateComponents.length - 1]?.dateFormat;
		if (dateFormat === undefined) {
			dateFormat = user?.Preferences?.DateFormat;
		}
		return dateFormat?.toUpperCase()!;
	}, [components, user?.Preferences?.DateFormat]);

	const generateComponentName = useCallback(
		(componentType: ComponentTypes): string => {
			const name = getDefaultComponentNames(componentType);
			const siblingCount = components.filter(c => c.type === componentType).length;

			return `${name} ${siblingCount + 1}`;
		},
		[components]
	);

	const initComponent = useCallback(
		(componentType: ComponentTypes, options: any): Component => {
			const componentName = generateComponentName(componentType);
			let component = { ...getDefaultComponent(componentType) } as Component;

			//Fill common required values
			component.id = `${uuidv4()}`;
			component.type = componentType;
			component.x = options.x;
			component.y = options.y;
			component.width = options.width;
			component.height = options.height;
			component.assignedTo = options.assignedTo;
			component.page = options.page;
			component.name = componentName;
			component.isAnnotation = isAnnotationComponent(componentType);
			component.sequence = options.sequence;

			const { selectedVariable, selectedProperty } = options;

			if (selectedVariable && selectedProperty) {
				component.name = [
					selectedVariable.provider,
					selectedVariable.name,
					selectedProperty.name,
				].join('.');
				component.variableId = selectedVariable.id;
				component.attributePath = selectedProperty.jsonPath;
				component.dataPreFilledType = DataPreFilledType.SourceMapped;
			}

			if (component.type === ComponentTypes.DateComponent) {
				component.dateFormat = getPreferredDateFormat();
			}

			if (ComponentTypes.CheckGroupComponent === component.type) {
				if (options.groupId === null) {
					const groupId = generateGroupId();
					component.groupId = groupId;
					component.groupRequirement = GroupRequirement.OnlyOne;
					component.name = `Checkbox Group ${groupId}`;
				} else {
					const {
						groupId,
						groupRequirement,
						name,
						assignedTo,
						isAnnotation,
						isRequired,
					} = components.find(component => component?.groupId === options.groupId);
					component = {
						...component,
						name,
						groupId,
						groupRequirement,
						assignedTo,
						isAnnotation,
						isRequired,
					};
				}
			}

			if (component.type === ComponentTypes.SignatureAnnotationComponent) {
				const signatureAnnotationComponents = components.filter(
					component =>
						component.type === ComponentTypes.SignatureAnnotationComponent &&
						component.signatureImage
				);

				if (signatureAnnotationComponents.length > 0) {
					const lastSignatureAnnotationComponent =
						signatureAnnotationComponents[signatureAnnotationComponents.length - 1];
					component.signatureImage = lastSignatureAnnotationComponent.signatureImage;
					component.signatureText = lastSignatureAnnotationComponent.signatureText;
					component.signatureType = lastSignatureAnnotationComponent.signatureType;
					component.signatureJsonData =
						lastSignatureAnnotationComponent.signatureJsonData;
				}
			}

			return component;
		},
		[components, generateComponentName, generateGroupId, getPreferredDateFormat]
	);

	const createComponent = useCallback(
		(
			x: number,
			y: number,
			pageId: string,
			componentType: ComponentTypes,
			groupId: string = null
		) => {
			const parentPos = getEditFlowElementById(pageId)?.getBoundingClientRect()!;
			const parentWidth = parentPos?.width ? parentPos.width : defaultWidth;
			const parentHeight = parentPos?.height ? parentPos.height : defaultHeight;

			const component = resizedComponents?.get(selectedComponentType as ComponentTypes);

			const { height: defaultComponentHeight, width: defaultComponentWidth } =
				defaultComponentDimensions[componentType];

			const height = component ? component.height * parentHeight : defaultComponentHeight;
			const width = component ? component.width * parentWidth : defaultComponentWidth;

			const borderWidth = 1;
			const sizeRatio = height / width;

			const scaledWidth = (width * parentWidth) / defaultWidth;
			const scaledHeight = scaledWidth * sizeRatio;

			let newX = x - parentPos.x;
			let newY = y - parentPos.y;

			newX =
				newX + scaledWidth > parentWidth
					? parentWidth - scaledWidth + borderWidth
					: newX + borderWidth;
			newY =
				newY + scaledHeight > parentHeight
					? parentHeight - scaledHeight + borderWidth
					: newY + borderWidth;

			const offset = componentType === ComponentTypes.CheckGroupComponent ? 0 : OFFSET_X;

			let maxSequnce = 0;
			if (groupId) {
				const componentSequences = components
					.filter(comp => comp.groupId === groupId)
					.map(comp => comp.sequence);

				if (componentSequences.length > 0) {
					maxSequnce = Math.max(...componentSequences) + 1;
				}
			}

			const newComponent = initComponent(componentType, {
				x: newX / (parentWidth + offset),
				y: newY / parentHeight,
				...getResizedWidthAndHeight(
					componentType,
					parentWidth,
					parentHeight,
					scaledWidth,
					scaledHeight
				),
				assignedTo: selectedProperty ? null : selectedSigner,
				page: Number(pageId.split('-')[2]),
				groupId,
				sequence: maxSequnce,
				selectedProperty,
				selectedVariable,
			});

			const newComponentsArray = [];

			newComponentsArray.push(newComponent);

			if (componentType === ComponentTypes.CheckGroupComponent && groupId === null) {
				const secondCheckBoxComponent = {
					...newComponent,
					sequence: maxSequnce + 1,
					id: uuidv4(),
					y: newComponent.y + newComponent.height + DEFAULT_GAP_CHECKBOXES / parentHeight,
				};
				newComponentsArray.push(secondCheckBoxComponent);
			}

			addNewComponent(newComponentsArray);

			return newComponent;
		},
		[
			addNewComponent,
			components,
			getResizedWidthAndHeight,
			initComponent,
			resizedComponents,
			selectedComponentType,
			selectedProperty,
			selectedSigner,
			selectedVariable,
		]
	);

	const addComponent = useCallback(
		(
			x: number,
			y: number,
			pageId: string,
			componentType: ComponentTypes = null,
			groupId: string = null
		) => {
			const component = createComponent(x, y, pageId, componentType, groupId);
			return component;
		},
		[createComponent]
	);

	const clearComponentForEdit = () => {
		setComponentsToEdit([]);
	};

	function handleComponentSelect(componentType: ComponentTypes | null) {
		setSelectedComponentType(componentType);
		clearComponentForEdit();
	}

	const getResizedComponents = useCallback(
		updatedComponent => {
			const newResizedComponents =
				resizedComponents || new Map<ComponentTypes, Dimensions>();

			newResizedComponents.set(updatedComponent.type, {
				width: updatedComponent.width,
				height: updatedComponent.height,
			});
			return newResizedComponents;
		},
		[resizedComponents]
	);
	const handlecomponentUpdate = useCallback(
		(updatedComponents: Component[], operation = 'update'): void => {
			const updatedComponentsIds = updatedComponents.map(comp => comp.id);

			let componentsCopy = components.filter(
				comp => !updatedComponentsIds.includes(comp.id)
			);

			const updatedComponentsToEdit = componentsToEdit.filter(
				componentToEdit => !updatedComponentsIds.includes(componentToEdit.id)
			);

			if (operation === 'update') {
				componentsCopy = [...componentsCopy, ...updatedComponents];
				setComponentsToEdit([...updatedComponentsToEdit, ...updatedComponents]);
			}
			if (
				operation === 'delete' &&
				updatedComponents[0].type !== ComponentTypes.CheckGroupComponent
			) {
				setComponentsToEdit([...updatedComponentsToEdit]);
			}

			updateDocumentTemplate({ fields: componentsCopy });
			setResizedComponents(getResizedComponents(updatedComponents[0]));
		},
		[
			components,
			componentsToEdit,
			getResizedComponents,
			setResizedComponents,
			updateDocumentTemplate,
		]
	);

	const onDropdownChange = useCallback(
		(value: string) => {
			const signer = signers?.find(signer => signer.id === value) || null;
			setSelectedSigner(signer);
			handleComponentSelect(null);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[signers]
	);

	function onSaveVariables() {
		if (componentsToEdit.length > 0) {
			const fieldIds =
				documentTemplate?.fields && documentTemplate?.fields.map(field => field.id);
			const hasDeletedField = componentsToEdit.some(
				component => fieldIds.indexOf(component.id) === -1
			);
			if (hasDeletedField) {
				clearComponentForEdit();
			}
		}
	}

	function onSelectVariable(variable: Variable) {
		setSelectedVariable(variable);
	}

	const onSelectField = (type: ComponentTypes | null) => {
		onComponentSelect(type);
	};

	function onComponentSelect(type: ComponentTypes | null) {
		if (selectedComponentType === type) {
			handleComponentSelect(null);
		} else {
			handleComponentSelect(type);
		}
	}

	const fields = useMemo(() => {
		const signerSequence = selectedSigner?.sequence ?? signers?.[0]?.sequence;
		const color = getSignerColor(signerSequence);
		const fields = typeof signerSequence === 'number' ? getSignerFields() : [];

		return fields.map(fieldValues => ({
			...fieldValues,
			color,
		}));
	}, [selectedSigner?.sequence, signers]);

	const scrollToPage = React.useCallback((pageNumber: number) => {
		setScrollToPageNumber(pageNumber);
	}, []);

	useEffect(() => {
		const listener = data => {
			const firstErrorComponent: Component = data.detail;
			if (firstErrorComponent) {
				setComponentsToEdit([firstErrorComponent]);
				const compNode = pdfRef.current.querySelector(
					'#component' + firstErrorComponent.id
				);
				compNode.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
			}
		};
		document.addEventListener(COMPONENT_INVALID_EVENT, listener);

		return () => {
			document.removeEventListener(COMPONENT_INVALID_EVENT, listener);
		};
	}, []);

	function addCheckbox() {
		const addCheckBoxElement = getEditFlowElementById('add-checkbox-to-group');
		const addCheckBoxElementRect = addCheckBoxElement.getBoundingClientRect();

		const pageElement = addCheckBoxElement.parentElement;

		const pageBoundingClientRect = pageElement.getBoundingClientRect();
		const resizedComponent = resizedComponents?.get(ComponentTypes.CheckGroupComponent);

		const { height: defaultComponentHeight, width: defaultComponentWidth } =
			defaultComponentDimensions[ComponentTypes.CheckGroupComponent];

		const componentHeight = resizedComponent
			? resizedComponent.height * pageBoundingClientRect.height
			: defaultComponentHeight;
		const componentWidth = resizedComponent
			? resizedComponent.width * pageBoundingClientRect.width
			: defaultComponentWidth;

		const leftOffset = 1; // Subtracting 1px from x to align checkbox because BoundingBorder has border of 2px

		const newX =
			addCheckBoxElementRect.x - leftOffset - pageBoundingClientRect.x + componentWidth <
			pageBoundingClientRect.width
				? addCheckBoxElementRect.x - leftOffset
				: addCheckBoxElementRect.x - leftOffset - componentWidth;

		const newY =
			addCheckBoxElementRect.y - pageBoundingClientRect.y + componentHeight <
			pageMetadata.pageHeight
				? addCheckBoxElementRect.y
				: addCheckBoxElementRect.y - componentHeight + addCheckBoxElementRect.height;

		const newComponent = addComponent(
			newX,
			newY,
			pageElement.id,
			ComponentTypes.CheckGroupComponent,
			componentsToEdit[0].groupId
		);
		setComponentsToEdit([newComponent]);
	}

	function removeCheckbox(componentToRemove: Component, index: number) {
		const checkobxGroupComponents = components.filter(
			comp =>
				comp.id !== componentToRemove.id && comp.groupId === componentToRemove.groupId
		);
		const mod = index % checkobxGroupComponents.length;
		setComponentsToEdit([checkobxGroupComponents[mod]]);
		handlecomponentUpdate([componentToRemove], 'delete');
	}

	const checkoboxesGroupId = componentsToEdit[0]?.groupId;
	const checkboxGroupComponents =
		(
			checkoboxesGroupId &&
			components.filter(
				comp =>
					comp.groupId === checkoboxesGroupId &&
					comp.type === ComponentTypes.CheckGroupComponent
			)
		)?.sort((a, b) => a?.sequence - b?.sequence) || [];

	const overlayFn = useCallback(
		pageMetadata => {
			setPageMetadata(pageMetadata);
			return (
				<OverlayerPage
					components={components}
					onComponentUpdate={handlecomponentUpdate}
					addComponent={addComponent}
					selectedComponents={componentsToEdit}
					onUpdateSelectedComponents={setComponentsToEdit}
					selectedField={selectedComponentType}
					onUpdateSelectedField={setSelectedComponentType}
					selectedSigner={selectedSigner}
					areFieldsEditable
					selectedProperty={selectedProperty}
					pageMetadata={pageMetadata}
				/>
			);
		},
		[
			addComponent,
			componentsToEdit,
			components,
			handlecomponentUpdate,
			selectedComponentType,
			selectedSigner,
			selectedProperty,
		]
	);

	function onSaveRole(role: Role) {
		setSelectedSigner(role);
		handleComponentSelect(null);
	}

	const variables = useMemo(() => {
		return documentTemplate?.variables || [];
	}, [documentTemplate?.variables]);

	useEffect(() => {
		if (!selectedSigner) {
			if (signers.length > 0) {
				setSelectedSigner(signers?.[0]);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [signers]);

	const elem: HTMLDivElement = document.querySelector(
		getDrawerBodyElementSelector(createDrawerBaseClassName)
	);

	useEffect(() => {
		let overflow = '';

		if (elem?.style) {
			overflow = elem.style.overflow;
			elem.style.overflow = 'hidden';
		}

		return () => {
			if (elem?.style) {
				elem.style.overflow = overflow;
			}
		};
	}, [elem?.style]);

	const containerHeight = elem?.clientHeight;

	return (
		<StyledPrepareDocumentContainer id={docgenFullScreenEditFlowId}>
			{contextHolder}
			<LeftPanel
				roles={signers}
				fields={fields}
				selectedRole={selectedSigner}
				selectedField={selectedComponentType}
				onSelectRole={onDropdownChange}
				onSelectField={onSelectField}
				onSaveRole={onSaveRole}
				variables={variables}
				onSaveVariables={onSaveVariables}
				onSelectVariable={onSelectVariable}
				selectedVariable={selectedVariable}
				selectedProperty={selectedProperty}
				setSelectedProperty={setSelectedProperty}
			/>
			<StyledDocumentViewer ref={pdfRef}>
				<PDFViewer
					pdf={dataUrl}
					containerHeight={containerHeight}
					scrollToPageNumber={scrollToPageNumber}
					overlay={overlayFn}
				/>
			</StyledDocumentViewer>
			<DocumentsPanel
				documentName={documentTemplate?.file?.fileName}
				pdf={dataUrl}
				onPageSelect={scrollToPage}
				selectedComponents={componentsToEdit}
				onClearSelectedComponent={clearComponentForEdit}
				onComponentUpdate={handlecomponentUpdate}
				checkboxGroupComponents={checkboxGroupComponents}
				addCheckbox={addCheckbox}
				removeCheckbox={removeCheckbox}
				onComponentSelect={setComponentsToEdit}
				variables={variables}
				containerHeight={containerHeight}
			/>
		</StyledPrepareDocumentContainer>
	);
};
